import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaTimes, FaSpinner, FaTrash, FaPaperclip } from 'react-icons/fa';
import { useNotification } from '../../contexts/NotificationContext';
import { newFinanceService, formatCurrencyTotals } from '../../services/newFinance.service';
import { parseFinanceInput } from '../../utils/financeParser';
import { storageService } from '../../services/storage.service';
import { getAuth } from 'firebase/auth';
import type {
  FinanceFormData,
  IncomeItem,
  OtherIncomeItem,
  ExpenseItem,
  Currency,
  CurrencyTotals,
  ExpenseCategory
} from '../../types/newFinance';
import { EXPENSE_CATEGORY_LABELS, CURRENCY_LABELS, OTHER_INCOME_CATEGORY_LABELS } from '../../types/newFinance';

const addRowShortcutLabel = 'F2';

const handleAddRowShortcut = (
  event: React.KeyboardEvent<HTMLElement>,
  addRow: () => void
) => {
  const nativeEvent = event.nativeEvent;
  const isF2 = event.key === 'F2' || event.code === 'F2' || nativeEvent.keyCode === 113 || nativeEvent.which === 113;
  if (!isF2) return;

  event.preventDefault();
  event.stopPropagation();
  addRow();
};

// 템플릿 타입 정의
interface FinanceTemplate {
  id: string;
  name: string;
  description?: string;
  category: 'weekly' | 'special' | 'custom';
  titheItems: Omit<IncomeItem, 'id'>[];
  thanksgivingItems: Omit<IncomeItem, 'id'>[];
  otherIncomeItems: Omit<OtherIncomeItem, 'id'>[];
  expenseItems: Omit<ExpenseItem, 'id'>[];
  createdAt: Date;
  usageCount?: number;
  lastUsed?: Date;
}

const TEMPLATE_CATEGORIES = {
  weekly: { label: '주간 고정', icon: '📅', color: 'blue' },
  special: { label: '특별행사', icon: '🎉', color: 'purple' },
  custom: { label: '사용자 정의', icon: '⚙️', color: 'green' }
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: string;
  onSuccess?: (formData?: FinanceFormData) => void;
  editMode?: boolean;
  initialData?: FinanceFormData;
}

export const FinanceInputModal: React.FC<Props> = ({
  isOpen,
  onClose,
  selectedDate,
  onSuccess,
  editMode = false,
  initialData
}) => {
  const { addNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentDate, setCurrentDate] = useState(selectedDate);

  // 템플릿 관련 상태
  const [templates, setTemplates] = useState<FinanceTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateCategory, setTemplateCategory] = useState<'weekly' | 'special' | 'custom'>('custom');
  const [selectedTemplateFilter, setSelectedTemplateFilter] = useState<'all' | 'weekly' | 'special' | 'custom'>('all');
  const [showTemplatePreview, setShowTemplatePreview] = useState<string | null>(null);

  // 폼 데이터
  const [titheItems, setTitheItems] = useState<Omit<IncomeItem, 'id'>[]>([]);
  const [thanksgivingItems, setThanksgivingItems] = useState<Omit<IncomeItem, 'id'>[]>([]);
  const [otherIncomeItems, setOtherIncomeItems] = useState<Omit<OtherIncomeItem, 'id'>[]>([]);
  const [expenseItems, setExpenseItems] = useState<Omit<ExpenseItem, 'id'>[]>([]);
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);

  // 초기 데이터 설정 - editMode와 initialData 처리
  useEffect(() => {
    if (isOpen) {
      setEvidenceFile(null);
      console.log('🔄 FinanceInputModal 열림');
      console.log('📝 editMode:', editMode);
      console.log('📋 initialData:', initialData);
      console.log('📅 selectedDate:', selectedDate);

      if (editMode && initialData) {
        console.log('✏️ 수정 모드 - 기존 데이터 로드 중...');
        console.log('🔢 십일조 항목:', initialData.titheItems);
        console.log('🙏 감사헌금 항목:', initialData.thanksgivingItems);
        console.log('💰 기타헌금 항목:', initialData.otherIncomeItems);
        console.log('💸 지출 항목:', initialData.expenseItems);

        // 수정 모드: 기존 데이터 로드
        const initialTitheItems = initialData.titheItems.length > 0 ? initialData.titheItems : [{ name: '', amount: 0, currency: 'HUF', description: '' }];
        const initialThanksItems = initialData.thanksgivingItems.length > 0 ? initialData.thanksgivingItems : [{ name: '', amount: 0, currency: 'HUF', description: '' }];
        const initialOtherItems = initialData.otherIncomeItems.length > 0 ? initialData.otherIncomeItems : [{ category: 'sunday', name: '', amount: 0, currency: 'HUF', description: '' }];
        const initialExpenseItems = initialData.expenseItems.length > 0 ? initialData.expenseItems : [{ category: 'agape', amount: 0, currency: 'HUF', description: '' }];

        setTitheItems(initialTitheItems as any);
        setThanksgivingItems(initialThanksItems as any);
        setOtherIncomeItems(initialOtherItems as any);
        setExpenseItems(initialExpenseItems as any);

        // 🇰🇷 모든 입력 필드에 강력한 한국어 입력 모드 설정
        setTimeout(() => {
          const allTextInputs = document.querySelectorAll('input[type="text"], textarea, [contenteditable]') as NodeListOf<HTMLInputElement>;
          allTextInputs.forEach(input => {
            // 강력한 한국어 입력 설정 패키지
            input.setAttribute('lang', 'ko-KR');
            input.setAttribute('inputmode', 'text');
            input.style.imeMode = 'active';
            input.style.webkitImeMode = 'active';
            input.setAttribute('spellcheck', 'false');
            // 부모 컨테이너에도 한국어 표시
            if (input.parentElement) {
              input.parentElement.setAttribute('data-korean-input', 'true');
            }
          });
          console.log('🇰🇷 모달 초기화: 모든 입력 필드 한국어 강제 설정 완료');
        }, 200);
        setCurrentDate(initialData.date);

        console.log('✅ 수정 모드 데이터 로드 완료');
      } else {
        console.log('➕ 새 입력 모드 - 빈 폼 초기화');
        // 새 입력 모드: 빈 폼
        setTitheItems([{ name: '', amount: 0, currency: 'HUF', description: '' } as any]);
        setThanksgivingItems([{ name: '', amount: 0, currency: 'HUF', description: '' } as any]);
        setOtherIncomeItems([{ category: 'sunday', name: '', amount: 0, currency: 'HUF', description: '' } as any]);
        setExpenseItems([{ category: 'agape', amount: 0, currency: 'HUF', description: '' } as any]);
        setCurrentDate(selectedDate);

        // 🇰🇷 모든 입력 필드에 강력한 한국어 입력 모드 설정
        setTimeout(() => {
          const allTextInputs = document.querySelectorAll('input[type="text"], textarea, [contenteditable]') as NodeListOf<HTMLInputElement>;
          allTextInputs.forEach(input => {
            // 강력한 한국어 입력 설정 패키지
            input.setAttribute('lang', 'ko-KR');
            input.setAttribute('inputmode', 'text');
            input.style.imeMode = 'active';
            input.style.webkitImeMode = 'active';
            input.setAttribute('spellcheck', 'false');
            // 부모 컨테이너에도 한국어 표시
            if (input.parentElement) {
              input.parentElement.setAttribute('data-korean-input', 'true');
            }
          });
          console.log('🇰🇷 모달 초기화: 모든 입력 필드 한국어 강제 설정 완료');
        }, 200);
      }
    }
  }, [isOpen, editMode, initialData, selectedDate]);

  // 합계 계산
  const calculateTotals = useCallback((items: { amount: number; currency: Currency }[]): CurrencyTotals => {
    return items.reduce((totals, item) => {
      totals[item.currency] += item.amount;
      return totals;
    }, { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 });
  }, []);

  const incomeTotals = calculateTotals([
    ...titheItems,
    ...thanksgivingItems,
    ...otherIncomeItems
  ]);

  const expenseTotals = calculateTotals(expenseItems);

  const netTotals: CurrencyTotals = {
    HUF: incomeTotals.HUF - expenseTotals.HUF,
    EUR: incomeTotals.EUR - expenseTotals.EUR,
    USD: incomeTotals.USD - expenseTotals.USD,
    KRW: incomeTotals.KRW - expenseTotals.KRW,
    GBP: incomeTotals.GBP - expenseTotals.GBP,
    CAD: incomeTotals.CAD - expenseTotals.CAD
  };

  // 템플릿 관련 함수들
  const loadTemplates = useCallback(() => {
    try {
      const savedTemplates = localStorage.getItem('financeTemplates');
      if (savedTemplates) {
        const parsed = JSON.parse(savedTemplates);
        // Date 객체 복원
        const templatesWithDates = parsed.map((template: any) => ({
          ...template,
          createdAt: new Date(template.createdAt)
        }));
        setTemplates(templatesWithDates);
      }
    } catch (error) {
      console.error('템플릿 로드 실패:', error);
    }
  }, []);

  const buildTemplate = (): FinanceTemplate => ({
      id: Date.now().toString(),
      name: templateName.trim(),
      description: templateDescription.trim(),
      category: templateCategory,
      titheItems: titheItems.filter(item => item.name || item.amount > 0),
      thanksgivingItems: thanksgivingItems.filter(item => item.name || item.amount > 0),
      otherIncomeItems: otherIncomeItems.filter(item => item.amount > 0),
      expenseItems: expenseItems.filter(item => item.description && item.amount > 0),
      createdAt: new Date(),
      usageCount: 0
  });

  const persistTemplates = (updatedTemplates: FinanceTemplate[]) => {
    setTemplates(updatedTemplates);
    localStorage.setItem('financeTemplates', JSON.stringify(updatedTemplates));
  };

  const saveTemplate = () => {
    if (!templateName.trim()) return addNotification('템플릿 이름을 입력해주세요.', 'error');
    const newTemplate = buildTemplate();
    persistTemplates([...templates, newTemplate]);
    setTemplateName('');
    setTemplateDescription('');
    setTemplateCategory('custom');
    setShowTemplateModal(false);
    addNotification(`템플릿 "${newTemplate.name}"이 저장되었습니다.`, 'success');
  };

  const loadTemplate = (template: FinanceTemplate) => {
    setTitheItems(template.titheItems.length ? template.titheItems : [{ name: '', amount: 0, currency: 'HUF', description: '' }]);
    setThanksgivingItems(template.thanksgivingItems.length ? template.thanksgivingItems : [{ name: '', amount: 0, currency: 'HUF', description: '' }]);
    setOtherIncomeItems(template.otherIncomeItems.length ? template.otherIncomeItems : [{ category: 'sunday', name: '', amount: 0, currency: 'HUF', description: '' }]);
    setExpenseItems(template.expenseItems.length ? template.expenseItems : [{ category: 'agape', amount: 0, currency: 'HUF', description: '' }]);
    persistTemplates(templates.map(t => t.id === template.id
      ? { ...template, usageCount: (template.usageCount || 0) + 1, lastUsed: new Date() }
      : t));
    addNotification(`템플릿 "${template.name}"을 불러왔습니다.`, 'success');
  };

  // 필터링된 템플릿 목록
  const filteredTemplates = templates.filter(template =>
    selectedTemplateFilter === 'all' || template.category === selectedTemplateFilter
  );

  // 템플릿 미리보기 생성
  const getTemplatePreview = (template: FinanceTemplate) => {
    const totalItems = template.titheItems.length + template.thanksgivingItems.length +
      template.otherIncomeItems.length + template.expenseItems.length;
    return `${totalItems}개 항목 • ${template.category === 'weekly' ? '주간고정' :
      template.category === 'special' ? '특별행사' : '사용자정의'}`;
  };

  const deleteTemplate = (templateId: string) => {
    const updatedTemplates = templates.filter(t => t.id !== templateId);
    setTemplates(updatedTemplates);
    localStorage.setItem('financeTemplates', JSON.stringify(updatedTemplates));
    addNotification('템플릿이 삭제되었습니다.', 'info');
  };

  // 컴포넌트 마운트 시 템플릿 로드
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, loadTemplates]);

  // 자동 파싱 처리
  const handleAutoParseAndAdd = (
    input: string,
    items: any[],
    setItems: (items: any[]) => void,
    index: number,
    isOtherIncome = false
  ) => {
    const parseResult = parseFinanceInput(input);

    if (parseResult.isValid) {
      const newItems = [...items];
      if (isOtherIncome) {
        newItems[index] = {
          ...newItems[index],
          amount: parseResult.amount,
          currency: parseResult.currency,
          description: parseResult.name
        };
      } else {
        newItems[index] = {
          ...newItems[index],
          name: parseResult.name,
          amount: parseResult.amount,
          currency: parseResult.currency
        };
      }

      const newRow = isOtherIncome
        ? { category: 'sunday', amount: 0, currency: 'HUF', description: '' }
        : { name: '', amount: 0, currency: 'HUF', description: '' };

      setItems([...newItems, newRow]);

      if (!isOtherIncome) {
        setTimeout(() => {
          // 정확한 테이블 찾기
          let targetSelector = '';
          if (items === titheItems) {
            targetSelector = 'h4:contains("십일조")';
          } else if (items === thanksgivingItems) {
            targetSelector = 'h4:contains("감사헌금")';
          }

          if (targetSelector) {
            const headers = Array.from(document.querySelectorAll('h4')).filter(h =>
              h.textContent?.includes(items === titheItems ? '십일조' : '감사헌금')
            );

            if (headers.length > 0) {
              const table = headers[0].closest('.bg-white')?.querySelector('table');
              const lastRow = table?.querySelector('tbody tr:last-child');
              const nameInput = lastRow?.querySelector('input[type="text"]:first-of-type') as HTMLInputElement;

              if (nameInput) {
                nameInput.focus();
                nameInput.select();
                nameInput.setAttribute('lang', 'ko');
                nameInput.setAttribute('inputmode', 'text');
              }
            }
          }
        }, 150);
      }
    }
  };

  // 스페이스바로 행 추가
  const handleNameInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    items: any[],
    setItems: (items: any[]) => void
  ) => {
    if (e.key === ' ') {
      e.preventDefault();
      const newRow = { name: '', amount: 0, currency: 'HUF', description: '' };
      setItems([...items, newRow]);

      setTimeout(() => {
        const tableIndex = items === titheItems ? 0 : 1;
        const tables = document.querySelectorAll('table');
        const lastRow = tables[tableIndex]?.querySelector('tbody tr:last-child');
        const nameInput = lastRow?.querySelector('input[type="text"]:first-of-type') as HTMLInputElement;

        if (nameInput) {
          nameInput.focus();
          nameInput.select();
          nameInput.setAttribute('lang', 'ko');
          nameInput.setAttribute('inputmode', 'text');
        }
      }, 100);
    }
  };

  // 행 추가
  const addRow = useCallback((targetTable: 'tithe' | 'thanksgiving' | 'otherIncome' | 'expense') => {
    let setItemsFunc: (items: any[]) => void;
    let defaultItem: any;

    switch (targetTable) {
      case 'tithe':
        setItemsFunc = setTitheItems;
        defaultItem = { name: '', amount: 0, currency: 'HUF', description: '' };
        break;
      case 'thanksgiving':
        setItemsFunc = setThanksgivingItems;
        defaultItem = { name: '', amount: 0, currency: 'HUF', description: '' };
        break;
      case 'otherIncome':
        setItemsFunc = setOtherIncomeItems;
        defaultItem = { category: 'sunday', name: '', amount: 0, currency: 'HUF', description: '' };
        break;
      case 'expense':
        setItemsFunc = setExpenseItems;
        defaultItem = { category: 'agape', amount: 0, currency: 'HUF', description: '' };
        break;
      default:
        return;
    }

    (setItemsFunc as any)((prevItems: any[]) => [...prevItems, defaultItem]);

    setTimeout(() => {
      // 🇰🇷 전체 모달의 모든 입력 필드에 한국어 설정 강제 적용
      const allInputs = document.querySelectorAll('input[type="text"], textarea, [contenteditable]') as NodeListOf<HTMLInputElement>;
      allInputs.forEach(input => {
        input.setAttribute('lang', 'ko-KR');
        input.setAttribute('inputmode', 'text');
        const style = input.style as any;
        style.imeMode = 'active';
        style.webkitImeMode = 'active';
        input.setAttribute('spellcheck', 'false');
      });

      // 텍스트 기반으로 정확한 테이블 찾기 및 포커스
      const headerTextMap = {
        tithe: '십일조',
        thanksgiving: '감사헌금',
        otherIncome: '기타헌금',
        expense: '지출'
      };

      const headerText = headerTextMap[targetTable];
      const headers = Array.from(document.querySelectorAll('h4')).filter(h =>
        h.textContent?.includes(headerText)
      );

      if (headers.length > 0) {
        const table = headers[0].closest('.bg-white, .dark\\:bg-gray-800')?.querySelector('table');
        const lastRow = table?.querySelector('tbody tr:last-child');

        if (targetTable === 'otherIncome' || targetTable === 'expense') {
          const firstSelect = lastRow?.querySelector('select') as HTMLSelectElement;
          if (firstSelect) firstSelect.focus();
        } else {
          const nameInput = lastRow?.querySelector('input[type="text"]:first-of-type') as HTMLInputElement;
          if (nameInput) {
            nameInput.focus();
            nameInput.setAttribute('lang', 'ko-KR');
            nameInput.setAttribute('inputmode', 'text');
          }
        }
      }

      console.log(`🇰🇷 [${targetTable}] 새 행 추가 및 포커스 완료`);
    }, 100);
  }, []);

  // 행 삭제
  const removeRow = (items: any[], setItems: (items: any[]) => void, index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  // 전역 저장 키보드 이벤트
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 저장 단축키 Cmd+Enter 또는 Ctrl+Enter
      const isSaveKey = (e.metaKey || e.ctrlKey) && e.key === 'Enter';

      if (isSaveKey) {
        e.preventDefault();
        // 폼 제출 트리거 (버튼 클릭 시뮬레이션 또는 직접 호출)
        const submitBtn = document.querySelector('button[type="submit"]') as HTMLButtonElement;
        if (submitBtn && !submitBtn.disabled) {
          submitBtn.click();
        }
      }
    };

    if (isOpen) {
      // 브라우저·입력 컴포넌트 핸들러보다 먼저 물리 F2 입력을 받습니다.
      window.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);

  const buildFormData = (): FinanceFormData => ({
        date: currentDate,
        titheItems: titheItems.filter(item => item.amount > 0),
        thanksgivingItems: thanksgivingItems.filter(item => item.amount > 0),
        otherIncomeItems: otherIncomeItems.filter(item => item.amount > 0),
        expenseItems: expenseItems.filter(item => item.amount > 0)
  });

  const saveEvidence = async (recordId: string) => {
    if (!evidenceFile) return;
    const safeFileName = evidenceFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `finance-evidence/${currentDate}/${recordId}/${safeFileName}`;
    const url = await storageService.uploadFile(evidenceFile, storagePath);
    const user = getAuth().currentUser;
    await newFinanceService.attachEvidence(recordId, {
      fileName: evidenceFile.name, url, path: storagePath,
      uploadedAt: new Date().toISOString(), uploadedBy: user?.displayName || user?.email || 'Unknown',
    });
  };

  const saveNewRecord = async (formData: FinanceFormData) => {
    const recordId = await newFinanceService.createFinanceRecord(formData);
    await saveEvidence(recordId);
    const formattedDate = new Date(currentDate).toLocaleDateString('ko-KR', {
      year: 'numeric', month: 'long', day: 'numeric', weekday: 'long'
    });
    addNotification(`${formattedDate} 재정입력이 저장되었습니다. 수고하셨습니다! 🎉`, 'success');
    onSuccess?.();
  };

  // 폼 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = buildFormData();
      if (editMode) onSuccess?.(formData);
      else await saveNewRecord(formData);
      onClose();
    } catch (error) {
      console.error('재정 데이터 저장 오류:', error);
      addNotification('재정 데이터 저장 중 오류가 발생했습니다.', 'error');
    } finally { setIsSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="finance-input-modal bg-white dark:bg-gray-800 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" data-korean-input="true">
        <form onSubmit={handleSubmit}>
          {/* 헤더 */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {editMode ? '재정 수정' : '재정 입력'}
                </h2>
                <p className="text-blue-100">
                  {currentDate} 재정 데이터를 {editMode ? '수정하세요' : '입력하세요'}
                </p>
              </div>

              {/* 템플릿 버튼들 */}
              <div className="flex items-center gap-3">
                {/* 템플릿 저장 */}
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(true)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 backdrop-blur-sm"
                  title="현재 입력을 템플릿으로 저장"
                >
                  템플릿 저장
                </button>

                {/* 템플릿 불러오기 드롭다운 - 업그레이드 */}
                {templates.length > 0 && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 backdrop-blur-sm"
                    >
                      템플릿 불러오기
                      <span className="bg-white bg-opacity-30 px-2 py-1 rounded-full text-xs font-medium">
                        {templates.length}
                      </span>
                    </button>
                    <div className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-600 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                      <div className="p-4">
                        {/* 카테고리 필터 */}
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            저장된 템플릿
                          </h3>
                          <div className="flex gap-1">
                            {(['all', 'weekly', 'special', 'custom'] as const).map((filter) => (
                              <button
                                key={filter}
                                type="button"
                                onClick={() => setSelectedTemplateFilter(filter)}
                                className={`px-2 py-1 text-xs rounded-full transition-colors ${selectedTemplateFilter === filter
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                  }`}
                              >
                                {filter === 'all' ? '전체' : TEMPLATE_CATEGORIES[filter].label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* 템플릿 목록 */}
                        <div className="max-h-80 overflow-y-auto space-y-2">
                          {filteredTemplates.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                              <p className="text-sm">해당 카테고리에 템플릿이 없습니다</p>
                            </div>
                          ) : (
                            filteredTemplates
                              .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0)) // 사용 빈도순 정렬
                              .map((template) => {
                                const categoryInfo = TEMPLATE_CATEGORIES[template.category];
                                return (
                                  <div key={template.id} className="group/item bg-gray-50 dark:bg-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0 mr-3">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-sm">{categoryInfo.icon}</span>
                                          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                            {template.name}
                                          </h4>
                                          {(template.usageCount || 0) > 0 && (
                                            <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs px-2 py-0.5 rounded-full">
                                              {template.usageCount}회 사용
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                          {getTemplatePreview(template)}
                                        </p>
                                        {template.description && (
                                          <p className="text-xs text-gray-500 dark:text-gray-400 italic truncate">
                                            "{template.description}"
                                          </p>
                                        )}
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                          생성: {template.createdAt instanceof Date ? template.createdAt.toLocaleDateString() : new Date(template.createdAt).toLocaleDateString()}
                                          {template.lastUsed && (
                                            <span className="ml-2">• 최근: {template.lastUsed instanceof Date ? template.lastUsed.toLocaleDateString() : new Date(template.lastUsed).toLocaleDateString()}</span>
                                          )}
                                        </p>
                                      </div>
                                      <div className="flex flex-col gap-1">
                                        <button
                                          type="button"
                                          onClick={() => setShowTemplatePreview(template.id)}
                                          className="p-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                          title="미리보기"
                                        >
                                          미리보기
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => loadTemplate(template)}
                                          className="p-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                          title="템플릿 불러오기"
                                        >
                                          불러오기
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteTemplate(template.id)}
                                          className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-400"
                                          title="템플릿 삭제"
                                        >
                                          <FaTrash className="h-3 w-3" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={onClose}
                  className="text-white hover:text-blue-200 transition-colors"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* 본문 */}
          <div className="p-6 space-y-8">
            {/* 날짜 선택 */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <label className="block text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                📅 날짜 선택
              </label>
              <input
                type="date"
                value={currentDate}
                onChange={(e) => setCurrentDate(e.target.value)}
                className="px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* 주간 재정 증빙자료: 원본은 OCR 없이 그대로 보관합니다. */}
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
              <label className="block text-sm font-medium text-amber-900 dark:text-amber-200 mb-2">
                <FaPaperclip className="inline mr-2" />주간 재정 증빙자료 (사진/PDF)
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (file && file.size > 10 * 1024 * 1024) {
                    addNotification('증빙파일은 10MB 이하만 업로드할 수 있습니다.', 'error');
                    e.currentTarget.value = '';
                    setEvidenceFile(null);
                    return;
                  }
                  setEvidenceFile(file);
                }}
                className="block w-full text-sm text-gray-700 dark:text-gray-200"
              />
              <p className="mt-2 text-xs text-amber-800 dark:text-amber-300">
                원본 자료로만 보관되며, 저장 후 금액을 직접 확인합니다. OCR은 실행하지 않습니다.
              </p>
              {evidenceFile && <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">선택됨: {evidenceFile.name}</p>}
            </div>

            {/* 수입 섹션 */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
                수입항목
              </h3>

              {/* 십일조 테이블 */}
              <div
                data-finance-table="tithe"
                onKeyDownCapture={(event) => handleAddRowShortcut(event, () => addRow('tithe'))}
              >
                <IncomeTable
                  title="십일조"
                  items={titheItems}
                  setItems={setTitheItems}
                  onAutoParseAndAdd={handleAutoParseAndAdd}
                  onAddRow={() => addRow('tithe')}
                  onRemoveRow={(index) => removeRow(titheItems, setTitheItems, index)}
                />
              </div>

              {/* 감사헌금 테이블 */}
              <div
                data-finance-table="thanksgiving"
                onKeyDownCapture={(event) => handleAddRowShortcut(event, () => addRow('thanksgiving'))}
              >
                <IncomeTable
                  title="감사헌금"
                  items={thanksgivingItems}
                  setItems={setThanksgivingItems}
                  onAutoParseAndAdd={handleAutoParseAndAdd}
                  onAddRow={() => addRow('thanksgiving')}
                  onRemoveRow={(index) => removeRow(thanksgivingItems, setThanksgivingItems, index)}
                />
              </div>

              {/* 기타헌금 테이블 */}
              <div
                data-finance-table="otherIncome"
                onKeyDownCapture={(event) => handleAddRowShortcut(event, () => addRow('otherIncome'))}
              >
                <OtherIncomeTable
                  title="기타헌금"
                  items={otherIncomeItems}
                  setItems={setOtherIncomeItems}
                  onAddRow={() => addRow('otherIncome')}
                  onRemoveRow={(index) => removeRow(otherIncomeItems, setOtherIncomeItems, index)}
                />
              </div>

              {/* 수입합계 */}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-green-800 dark:text-green-200">
                    수입합계
                  </span>
                  <span className="text-xl font-bold text-green-800 dark:text-green-200">
                    {formatCurrencyTotals(incomeTotals)}
                  </span>
                </div>
              </div>
            </div>

            {/* 지출 섹션 */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <span className="w-3 h-3 bg-red-500 rounded-full mr-3"></span>
                지출항목
              </h3>

              <div
                data-finance-table="expense"
                onKeyDownCapture={(event) => handleAddRowShortcut(event, () => addRow('expense'))}
              >
                <ExpenseTable
                  items={expenseItems}
                  setItems={setExpenseItems}
                  onAddRow={() => addRow('expense')}
                  onRemoveRow={(index) => removeRow(expenseItems, setExpenseItems, index)}
                />
              </div>

              {/* 지출합계 */}
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-red-800 dark:text-red-200">
                    지출합계
                  </span>
                  <span className="text-xl font-bold text-red-800 dark:text-red-200">
                    {formatCurrencyTotals(expenseTotals)}
                  </span>
                </div>
              </div>
            </div>

            {/* 총액 */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border-2 border-blue-200 dark:border-blue-700">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-lg">
                  <span className="font-semibold text-blue-800 dark:text-blue-200">총수입</span>
                  <span className="font-bold text-blue-800 dark:text-blue-200">
                    {formatCurrencyTotals(incomeTotals)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-lg">
                  <span className="font-semibold text-blue-800 dark:text-blue-200">총지출</span>
                  <span className="font-bold text-blue-800 dark:text-blue-200">
                    {formatCurrencyTotals(expenseTotals)}
                  </span>
                </div>
                <div className="border-t-2 border-blue-300 dark:border-blue-600 pt-2">
                  <div className="flex items-center justify-between text-xl">
                    <span className="font-bold text-blue-900 dark:text-blue-100">순손익</span>
                    <span className="font-bold text-blue-900 dark:text-blue-100">
                      {formatCurrencyTotals(netTotals)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 푸터 */}
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-2xl">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    저장 중...
                  </>
                ) : (
                  editMode ? '수정 완료' : '저장'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* 템플릿 저장 모달 */}
      {showTemplateModal && (
        <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-60">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full mx-4 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  템플릿 저장
                </h3>
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    템플릿 이름 *
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="예: 주일예배 기본템플릿"
                    lang="ko-KR"
                    inputMode="text"
                    autoComplete="off"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    카테고리 *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(TEMPLATE_CATEGORIES).map(([key, info]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setTemplateCategory(key as 'weekly' | 'special' | 'custom')}
                        className={`p-3 rounded-lg border-2 transition-all duration-200 ${templateCategory === key
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                      >
                        <div className="text-center">
                          <div className="text-lg mb-1">{info.icon}</div>
                          <div className="text-xs font-medium">{info.label}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {templateCategory === 'weekly' && '매주 반복되는 고정 항목들'}
                    {templateCategory === 'special' && '특별행사나 이벤트용 항목들'}
                    {templateCategory === 'custom' && '사용자가 직접 구성한 항목들'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    설명 (선택사항)
                  </label>
                  <textarea
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder="템플릿에 대한 간단한 설명..."
                    rows={3}
                    lang="ko-KR"
                    inputMode="text"
                    autoComplete="off"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  className="px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={saveTemplate}
                  disabled={!templateName.trim()}
                  className="px-5 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 템플릿 미리보기 모달 */}
      {showTemplatePreview && (
        <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-60">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto">
            {(() => {
              const template = templates.find(t => t.id === showTemplatePreview);
              if (!template) return null;
              const categoryInfo = TEMPLATE_CATEGORIES[template.category];

              return (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{categoryInfo.icon}</span>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {template.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {categoryInfo.label} • {getTemplatePreview(template)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowTemplatePreview(null)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <FaTimes className="h-5 w-5" />
                    </button>
                  </div>

                  {template.description && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm text-blue-800 dark:text-blue-200 italic">
                        "{template.description}"
                      </p>
                    </div>
                  )}

                  <div className="space-y-6">
                    {/* 십일조 미리보기 */}
                    {template.titheItems.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          📊 십일조 ({template.titheItems.length}개)
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {template.titheItems.map((item, index) => (
                            <div key={index} className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-700">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                  {item.name || '(이름 없음)'}
                                </span>
                                <span className="text-sm text-green-600 dark:text-green-400">
                                  {item.amount.toLocaleString()} {item.currency}
                                </span>
                              </div>
                              {item.description && (
                                <p className="text-xs text-green-600 dark:text-green-400 mt-1 italic">
                                  "{item.description}"
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 감사헌금 미리보기 */}
                    {template.thanksgivingItems.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          🙏 감사헌금 ({template.thanksgivingItems.length}개)
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {template.thanksgivingItems.map((item, index) => (
                            <div key={index} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                  {item.name || '(이름 없음)'}
                                </span>
                                <span className="text-sm text-blue-600 dark:text-blue-400">
                                  {item.amount.toLocaleString()} {item.currency}
                                </span>
                              </div>
                              {item.description && (
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 italic">
                                  "{item.description}"
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 기타헌금 미리보기 */}
                    {template.otherIncomeItems.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          ✨ 기타헌금 ({template.otherIncomeItems.length}개)
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {template.otherIncomeItems.map((item, index) => (
                            <div key={index} className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
                                  {OTHER_INCOME_CATEGORY_LABELS[item.category]}
                                </span>
                                <span className="text-sm text-purple-600 dark:text-purple-400">
                                  {item.amount.toLocaleString()} {item.currency}
                                </span>
                              </div>
                              {item.description && (
                                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 italic">
                                  "{item.description}"
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 지출 미리보기 */}
                    {template.expenseItems.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          💸 지출 ({template.expenseItems.length}개)
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {template.expenseItems.map((item, index) => (
                            <div key={index} className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-700">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-red-800 dark:text-red-200">
                                  {EXPENSE_CATEGORY_LABELS[item.category]}
                                </span>
                                <span className="text-sm text-red-600 dark:text-red-400">
                                  {item.amount.toLocaleString()} {item.currency}
                                </span>
                              </div>
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1 italic">
                                "{item.description}"
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                    <button
                      type="button"
                      onClick={() => setShowTemplatePreview(null)}
                      className="px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      닫기
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        loadTemplate(template);
                        setShowTemplatePreview(null);
                      }}
                      className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    >
                      이 템플릿 사용하기
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

// 수입 테이블 컴포넌트
interface IncomeTableProps {
  title: string;
  items: Omit<IncomeItem, 'id'>[];
  setItems: (items: Omit<IncomeItem, 'id'>[]) => void;
  onAutoParseAndAdd: (input: string, items: any[], setItems: any, index: number) => void;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
}

const IncomeTable: React.FC<IncomeTableProps> = ({
  title,
  items,
  setItems,
  onAutoParseAndAdd,
  onAddRow,
  onRemoveRow
}) => {
  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between">
        <h4 className="font-semibold text-gray-900 dark:text-white">{title}</h4>
        <button
          type="button"
          onClick={onAddRow}
          className="flex items-center px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded transition-colors"
        >
          <FaPlus className="mr-1" /> {addRowShortcutLabel}: 행추가
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600">
            <tr>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">이름</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">금액</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">통화</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">내용</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 w-16">삭제</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-t border-gray-200 dark:border-gray-600">
                <td className="px-4 py-3">
                    <input
                    type="text"
                      autoFocus={title === '십일조' && index === 0}
                    value={(item as any).name || ''}
                    onChange={(e) => updateItem(index, 'name' as any, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === ' ') {
                        e.preventDefault();
                        onAutoParseAndAdd(e.currentTarget.value, items, setItems, index);
                      }
                    }}
                    placeholder="김철수10000ㄷ + 스페이스"
                    lang="ko-KR"
                    inputMode="text"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    value={item.amount || ''}
                    onChange={(e) => updateItem(index, 'amount', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </td>
                <td className="px-4 py-3">
                  <select
                    value={item.currency}
                    onChange={(e) => updateItem(index, 'currency', e.target.value as Currency)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {Object.entries(CURRENCY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={(item as any).description || ''}
                    onChange={(e) => updateItem(index, 'description' as any, e.target.value)}
                    placeholder="내용"
                    lang="ko-KR"
                    inputMode="text"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => onRemoveRow(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <FaTrash className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 기타헌금 테이블 컴포넌트 (설명 필드 추가)
interface OtherIncomeTableProps {
  title: string;
  items: Omit<OtherIncomeItem, 'id'>[];
  setItems: (items: Omit<OtherIncomeItem, 'id'>[]) => void;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
}

const OtherIncomeTable: React.FC<OtherIncomeTableProps> = ({
  title,
  items,
  setItems,
  onAddRow,
  onRemoveRow
}) => {
  const updateItem = (index: number, field: keyof Omit<OtherIncomeItem, 'id'>, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between">
        <h4 className="font-semibold text-gray-900 dark:text-white">{title}</h4>
        <button
          type="button"
          onClick={onAddRow}
          className="flex items-center px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded transition-colors"
        >
          <FaPlus className="mr-1" /> {addRowShortcutLabel}: 행추가
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600">
            <tr>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">항목</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">이름</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">금액</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">통화</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">내용</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 w-16">삭제</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-t border-gray-200 dark:border-gray-600">
                <td className="px-4 py-3">
                  <select
                    value={item.category}
                    onChange={(e) => updateItem(index, 'category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {Object.entries(OTHER_INCOME_CATEGORY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                    placeholder="이름 입력"
                    lang="ko-KR"
                    inputMode="text"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    value={item.amount || ''}
                    onChange={(e) => updateItem(index, 'amount', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </td>
                <td className="px-4 py-3">
                  <select
                    value={item.currency}
                    onChange={(e) => updateItem(index, 'currency', e.target.value as Currency)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="HUF">HUF</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="KRW">KRW</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="직접 입력"
                    lang="ko-KR"
                    inputMode="text"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => onRemoveRow(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <FaTrash className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 지출 테이블 컴포넌트
interface ExpenseTableProps {
  items: Omit<ExpenseItem, 'id'>[];
  setItems: (items: Omit<ExpenseItem, 'id'>[]) => void;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
}

const ExpenseTable: React.FC<ExpenseTableProps> = ({
  items,
  setItems,
  onAddRow,
  onRemoveRow
}) => {
  const updateItem = (index: number, field: keyof Omit<ExpenseItem, 'id'>, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between">
        <h4 className="font-semibold text-gray-900 dark:text-white">지출 내역</h4>
        <button
          type="button"
          onClick={onAddRow}
          className="flex items-center px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded transition-colors"
        >
          <FaPlus className="mr-1" /> {addRowShortcutLabel}: 행추가
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600">
            <tr>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">항목</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">금액</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">통화</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">내용</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">지급방법</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">상환</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 w-16">삭제</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-t border-gray-200 dark:border-gray-600">
                <td className="px-4 py-3">
                  <select
                    value={item.category}
                    onChange={(e) => updateItem(index, 'category', e.target.value as ExpenseCategory)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {Object.entries(EXPENSE_CATEGORY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    value={item.amount || ''}
                    onChange={(e) => updateItem(index, 'amount', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </td>
                <td className="px-4 py-3">
                  <select
                    value={item.currency}
                    onChange={(e) => updateItem(index, 'currency', e.target.value as Currency)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {Object.entries(CURRENCY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="상세 내용"
                    lang="ko-KR"
                    inputMode="text"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </td>
                <td className="px-4 py-3">
                  <select
                    value={(item as any).paymentSource || 'church-cash'}
                    onChange={(e) => updateItem(index, 'paymentSource' as any, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="church-cash">교회 현금</option>
                    <option value="personal-advance">개인 선지출</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean((item as any).reimbursed)}
                    disabled={(item as any).paymentSource !== 'personal-advance'}
                    onChange={(e) => updateItem(index, 'reimbursed' as any, e.target.checked)}
                    title="개인 선지출을 이미 상환했으면 선택"
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => onRemoveRow(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <FaTrash className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
